import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppIcon, PageHeader } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SEARCH_PAGE_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { fetchNuggetsUserHasSearched } from '@backrs_ui/grow-ui/src/pages/Search/search.util';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setSearchTermForCategory } from '@backrs_ui/grow-ui/src/redux/search/search';
import { selectSharedSessionOwnerFirstName } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';

import { Box, Button, TextField, SxProps, Theme } from '@mui/material';

const STRINGS = appStrings.modules.careerPath.summaryPage;

type CareerPathHeaderProps = Readonly<{
  userHasBlueprint: boolean;
  isShared?: boolean;
}>;

export const CareerPathHeader = ({ userHasBlueprint, isShared = false }: CareerPathHeaderProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const sharedSessionUUID = params.uuid;
  const [searchTerm, setSearchTerm] = useState('');
  const sharedOwnerFirstName = useAppSelector(selectSharedSessionOwnerFirstName);

  const handleSearchEnterKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && searchTerm.trim()) {
      dispatch(
        setSearchTermForCategory({
          category: SEARCH_PAGE_CATEGORIES.DEFAULT,
          searchTerm: searchTerm.trim(),
        }),
      );
      fetchNuggetsUserHasSearched(dispatch, searchTerm.trim(), 1);
      navigate(APP_ROUTE.SEARCH);
      setSearchTerm('');
    }
  };

  const handleSearchIconClick = () => {
    navigate(APP_ROUTE.SEARCH);
  };

  const handleMyBlueprintClick = () => {
    if (isShared) {
      navigate(APP_ROUTE.SHARED_MY_BLUEPRINT.replace(':uuid', sharedSessionUUID || ''));

      return;
    }

    if (userHasBlueprint) {
      navigate(APP_ROUTE.MY_BLUEPRINT);

      return;
    }

    navigate(APP_ROUTE.SURVEY);
  };

  const actionButtons = (
    <Box sx={actionButtonsContainerStyle}>
      <Button
        id="career-path-tour-button"
        variant="outlined"
        size="md"
        hierarchy="Secondary gray"
        disabled
        sx={tourButtonStyle}
      >
        {STRINGS.tour}
      </Button>

      <Button
        id="career-path-my-blueprint-button"
        variant="contained"
        size="md"
        hierarchy="Primary"
        onClick={handleMyBlueprintClick}
        sx={blueprintButtonStyle}
      >
        {STRINGS.myBlueprint(isShared ? sharedOwnerFirstName : undefined)}
      </Button>
    </Box>
  );

  const searchBar = (
    <TextField
      id="career-path-search-input"
      variant="outlined"
      placeholder={STRINGS.search}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      onKeyDown={handleSearchEnterKey}
      sx={searchBarStyle}
      InputProps={{
        startAdornment: (
          <Box
            id="career-path-search-icon"
            onClick={handleSearchIconClick}
            sx={searchIconContainerStyle}
          >
            <AppIcon
              iconName="SearchMd"
              diameter="20px"
              color="Colors/Background/bg-primary-solid"
            />
          </Box>
        ),
      }}
    />
  );

  return (
    <Box sx={containerSx}>
      <PageHeader
        title={STRINGS.title}
        subtitle={STRINGS.description}
        rightComponents={isShared ? [actionButtons] : [actionButtons, searchBar]}
        maxWidth={null}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const actionButtonsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const tourButtonStyle: SxProps<Theme> = {
  minWidth: 'fit-content',
};

const blueprintButtonStyle: SxProps<Theme> = {
  minWidth: 'fit-content',
};

const searchBarStyle: SxProps<Theme> = () => ({
  flex: 1,
  minWidth: '200px',
  width: '100%',
  '& .MuiInputBase-root': {
    height: '48px',
  },
});

const searchIconContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
};
